import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import PartCard from "@/components/PartCard";
import { useNavigate } from "react-router-dom";
import {
  fetchCategories,
  fetchPartsByCategories,
  filterCategories
} from "../store/categories/categoriesAction";
import { useDispatch, useSelector } from "react-redux";
import { Loader2 } from "lucide-react";
import FilterDropdown from "../components/FilterDropdown";
import CardShimmer from "../hooks/CardShimmer";

const CategoriesPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [currentCategory, setCurrentCategory] = useState("all");
  const [listingsLoading, setListingsLoading] = useState(false);

  const {
    categories,
    partsByCategory,
    categoriesLoading,
    categoriesLoadingMore,
    categoriesLastVisible,
    categoriesHasMore,
    filteredParts,
    filteredCategory,
    filtering,
    partsMapping
  } = useSelector((state) => state.categories);

  const handleLoadMore = async () => {
    try {
      await dispatch(
        fetchCategories({
          lastVisible: categoriesLastVisible,
          isLoadMore: true
        })
      ).unwrap();

      if (categories?.length) {
        dispatch(fetchPartsByCategories(categories));
      }
    } catch (error) {
      console.error("Load more error:", error);
    }
  };

  const handleNavigateToCategory = (category) => {
    const route = `/listings/category/${category?.id}`;
    navigate(route);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setListingsLoading(() => true);
        const result = await dispatch(
          fetchCategories({ lastVisible: null, isLoadMore: false })
        ).unwrap();

        if (result?.categories?.length) {
          dispatch(fetchPartsByCategories(result?.categories));
        }
      } catch (error) {
        console.error("Error in fetching data on CategoriesPage:", error);
      } finally {
        setListingsLoading(() => false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (currentCategory === "all") return;
    dispatch(filterCategories(currentCategory)).unwrap();
  }, [currentCategory]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.05 }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 100 }
    }
  };

  const filteredCategories =
    currentCategory === "all" ? categories : filteredCategory;

  return (
    <motion.div
      className="space-y-12 mt-5 w-full"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <>
        <motion.h1
          className="text-3xl md:text-4xl font-bold text-center mb-8 bg-gradient-to-r from-primary to-accent text-transparent bg-clip-text pb-1"
          variants={itemVariants}
        >
          Listings
        </motion.h1>
        {listingsLoading ? (
          <div className="py-10">
            <CardShimmer row={1} />
          </div>
        ) : (
          <motion.div className="w-full flex justify-end">
            <FilterDropdown
              currentCategory={currentCategory}
              setCurrentCategory={setCurrentCategory}
            />
          </motion.div>
        )}
        {filtering && !listingsLoading ? (
          <CardShimmer row={1} />
        ) : (
          <>
            <div className="space-y-10">
              {!listingsLoading &&
                filteredCategories?.map((category) => (
                  <motion.section
                    key={category.id}
                    variants={itemVariants}
                    className="border-b pb-10 last:border-b-0"
                  >
                    <div className="flex justify-between items-center mb-6">
                      <h2 className="text-lg xs:text-2xl font-semibold flex items-center gap-3">
                        {category.name}
                      </h2>
                      <Button
                        variant="link"
                        size="sm"
                        className="text-primary"
                        onClick={() => handleNavigateToCategory(category)}
                      >
                        <span className="hidden sm:inline">
                          View All in {category.name}
                        </span>
                        <span className="hidden xs:inline sm:hidden">
                          View All
                        </span>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={2}
                          stroke="currentColor"
                          className="w-4 h-4 ml-1"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3"
                          />
                        </svg>
                      </Button>
                    </div>

                    {(() => {
                      const partsToRender =
                        currentCategory === "all"
                          ? partsByCategory?.[category?.name] || []
                          : filteredParts || [];

                      if (partsMapping && !listingsLoading) {
                        return (
                          <>
                            <CardShimmer row={1} />
                          </>
                        );
                      }

                      if (!partsToRender.length) {
                        return (
                          <p className="text-muted-foreground text-md">
                            No parts available for this category yet.
                          </p>
                        );
                      }

                      return (
                        <motion.div
                          className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6"
                          variants={containerVariants}
                        >
                          {!listingsLoading &&
                            partsToRender.map((part) => (
                              <PartCard key={part.id} part={part} />
                            ))}
                        </motion.div>
                      );
                    })()}
                  </motion.section>
                ))}
            </div>
            {categoriesHasMore && currentCategory === "all" && (
              <motion.div className="flex items-center justify-center">
                <Button
                  variant="outline"
                  className="px-6 py-2"
                  onClick={handleLoadMore}
                  disabled={categoriesLoadingMore || listingsLoading}
                >
                  {categoriesLoadingMore && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  {categoriesLoadingMore ? "Loading..." : "Load More"}
                </Button>
              </motion.div>
            )}
          </>
        )}
      </>
    </motion.div>
  );
};

export default CategoriesPage;
